import { removeTimeZone } from "@swa-ui/date";

/**
 * Transforms NextGen flight selections into CarTrawler flight leg data structure.
 * Accepts an array of bounds, where each bound is an array of segment objects,
 * and normalizes them before building the legs.
 *
 * @param {Array[]} flightSegments Array of bounds, each bound being an array of segment objects.
 * @returns {Object} Flight legs formatted as per CarTrawler data structure.
 */
export const getFlightInformationForCarSection = (flightSegments = []) => {
  const fares = getNormalizedSegmentsForCarSection(flightSegments);
  const legs = [];
  const outboundLastStop = fares?.[0]?.stopsDetails?.at(-1);

  if (outboundLastStop) {
    const outboundLeg = getSegmentLeg(outboundLastStop, fares[0].operatingCarrierCode, "outbound");

    legs.push(outboundLeg);
  }

  const inboundFirstStop = fares?.[1]?.stopsDetails?.at(0);

  if (inboundFirstStop) {
    const inboundLeg = getSegmentLeg(inboundFirstStop, fares[1].operatingCarrierCode, "inbound");

    legs.push(inboundLeg);
  }

  return {
    legs,
  };
};

/**
 * Normalizes NextGen-shaped flight bounds into the flat fare structure expected by
 * {@link getFlightInformationForCarSection}. Each bound is an array of
 * segment objects whose `stopsDetails` arrays are concatenated onto the first segment.
 *
 * @param {Array[]} flightSegments Array of bounds, each bound being an array of segment objects.
 * @returns {Array} Normalized fare objects, one per bound, each with a merged `stopsDetails` array.
 */
const getNormalizedSegmentsForCarSection = (flightSegments = []) =>
  flightSegments.map(([firstSegment, ...rest]) => ({
    ...firstSegment,
    stopsDetails: [
      ...(firstSegment?.stopsDetails ?? []),
      ...rest.flatMap((s) => s.stopsDetails ?? []),
    ],
  }));

/**
 * Builds a single CarTrawler flight leg object from a stop and its carrier info.
 *
 * @param {Object} stop - A stop details object from the normalized fare's `stopsDetails` array.
 * @param {string} operatingCarrierCode - IATA carrier code (e.g. "WN") from the first segment of the bound.
 * @param {string} key - Leg direction identifier, either "outbound" or "inbound".
 * @returns {Object} A CarTrawler-formatted flight leg.
 */
const getSegmentLeg = (stop, operatingCarrierCode, key) => {
  const segmentLeg = {
    arrival: {
      datetime: removeTimeZone(stop.arrivalDateTime),
      iata: stop.destinationAirportCode,
    },
    departure: {
      datetime: removeTimeZone(stop.departureDateTime),
      iata: stop.originationAirportCode,
    },
    flightNo: `${operatingCarrierCode}${stop.flightNumber}`,
    key,
  };

  return segmentLeg;
};
